<template>
	<div class="search-suggester search-suggester--web" :class="{ light: isLightTheme }">
		<div class="searchbar-container">
			<SearchBar
				ref="searchbar"
				:placeholder="placeholder"
				:model-value="searchQuery"
				@update:model-value="handleSearch"
				@submit="() => navigateToSearchPage('enter')"
				@focus="openSearchBar"
				@arrow-down="() => move('down')"
			/>
		</div>

		<div class="search-suggester__results" v-if="isSearchSuggesterOpen">
			<SearchSuggesterResults
				ref="searchResults"
				@showAllResults="() => navigateToSearchPage('show_all')"
				@cancel="closeSearchBar"
				@editSearch="editSearch"
				@arrowNavigation="move"
			/>
		</div>

		<div class="search-overlay" v-if="isSearchSuggesterOpen" @click="() => closeSearchBar(true)" />
	</div>
</template>

<script setup lang="ts">
import type { RouteLocationNormalized } from 'vue-router';

import { CollectionType } from '@/enums/collection-type';

import SearchBar from '@/features/search/components/SearchBar.vue';

import { useSearch } from '@/features/search/composables/useSearch';
import { useSearchTracking } from '@/features/search/tracking';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

import { useMounted, useMediaQuery } from '@vueuse/core';
import { computed, onMounted, ref, watch, useTemplateRef } from 'vue';

const SearchSuggesterResults = defineAsyncComponent(
	() => import('@/features/search/components/SearchSuggesterResults.vue')
);

const emit = defineEmits<{ cancel: [] }>();

const routingStore = useRoutingStore();

const {
	isSearchSuggesterOpen,
	search,
	personId,
	setSearchSuggesterVisibility,
	query: searchQuery,
	goToSearch,
	searchTitleContexts,
} = useSearch();

const { t } = useI18n();
const { trackSearch } = useSearchTracking();
const isMounted = useMounted();

const route = useRoute();
// Intialize query and person_id from URL if params are present
onMounted(() => {
	if (route.query.q != null && searchQuery.value == '') {
		search(route.query.q.toString());
	}

	if (route.query.person_id != null && personId.value == null) {
		const person_id = Number(route.query.person_id);
		if (!isNaN(person_id)) personId.value = person_id;
	}
});

const searchbar = useTemplateRef<InstanceType<typeof SearchBar>>('searchbar');
const searchResults = useTemplateRef<InstanceType<typeof SearchSuggesterResults>>('searchResults');

const isMobileViewport = useMediaQuery('(max-width: 961px)');
const placeholder = computed(() => {
	if (!isMounted.value) return t('WEBAPP_SEARCH');

	return isMobileViewport.value ? t('WEBAPP_SEARCH') : t('WEBAPP_SEARCH_PLACEHOLDER');
});

watch(
	() => routingStore.lastActiveCollectionType,
	newCollection => {
		if (newCollection !== CollectionType.SEARCH) emit('cancel');
	}
);

/** close results when the route has been changed. */
watch(
	() => routingStore.activeRoute,
	(route: RouteLocationNormalized | null) => {
		closeSearchBar(true);

		// if the route has changed to a persons page, replace the input with the proper name of the person
		if (route?.query.person_id && searchbar.value?.searchBarInput) {
			search(route.query.q as string);
		}
	}
);

const isLightTheme = computed(() => {
	return false;
	//return country.value === 'GB' && activeRoute.value.path === '/' && !isUserOnboarded.value;
});

async function openSearchBar() {
	if (isSearchSuggesterOpen.value) return;

	// INP case: artificially give the JS thread time to breathe by adding a re-render.
	await new Promise(resolve => setTimeout(resolve, 0));

	search(searchQuery.value);
	setSearchSuggesterVisibility(true);
}

function closeSearchBar(forceClose: boolean = false) {
	// if user is on search, we want to keep the search bar
	//if user navigates to title page from search, close
	if (routingStore.lastActiveCollectionType !== CollectionType.SEARCH || forceClose) {
		emit('cancel');
		setSearchSuggesterVisibility(false);
	}
}

function editSearch(searchTerm?: string) {
	if (searchbar.value?.searchBarInput == null) return;

	searchbar.value?.searchBarInput?.focus();
	search(searchTerm ?? '');
}

/* KEYBOARD NAVIGATION AND FOCUS */
const index = ref(-1);

function handleSearch(query: string) {
	index.value = -1;
	search(query);
}

function move(direction: 'up' | 'down') {
	const items = searchResults.value?.resultItems ?? [];
	if (!items.length) return;

	if (direction === 'down') {
		index.value = (index.value + 1) % items.length;
	} else {
		index.value = Math.max(-1, index.value - 1);
	}

	if (index.value === -1) {
		searchbar.value?.searchBarInput?.focus();
		return;
	}

	items[index.value]?.resultItem?.focus?.();
}

function navigateToSearchPage(label: 'enter' | 'show_all') {
	goToSearch({ searchString: searchQuery.value }, false);

	trackSearch('submitSearch', { spec: { label }, contexts: searchTitleContexts.value });
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

.search-suggester__results {
	overflow: auto;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
	max-height: 90vh;
}

.search-suggester {
	position: relative;
	.sponsored-recommendation-search {
		margin-top: -10px;
		z-index: 1000;
		position: relative;
		display: flex;
		width: 100%;
	}

	input[type='search'] {
		&::-webkit-search-cancel-button {
			display: none;
		}
	}

	&__results {
		z-index: 200;
		padding: 0 8px 8px;
		margin: -13px 0 0;

		&--mobile {
			padding: 0;
			margin: -11px -1em -0.4em;
		}
	}
	&--web {
		text-align: start;
		width: 100%;
		padding: 0;
		height: 52px;
		z-index: 10;

		&.light {
			.search-suggester,
			.search-suggester-results,
			.search-suggester__footer {
				background: var(--foreground-foreground-ghost, rgba(255, 255, 255, 0.04));
				backdrop-filter: blur(100px);
			}
			.search-bar__wrapper {
				background: #d2d3d517;
				backdrop-filter: blur(100px);

				.search-bar__search-icon {
					color: #d2d3d5;
				}

				input::placeholder {
					color: #d2d3d5 !important;
				}
			}
		}

		&.small-search-icon {
			display: flex;
			align-items: flex-end;
			justify-content: center;
			flex-direction: column;
			flex: 1 !important;
		}
	}

	&__searchbar {
		&--mobile {
			padding-top: 3px !important;
			padding-bottom: 10px !important;

			&.md {
				height: 52px;
				--background: #{$jw-color-white};
				--color: var(--jw-color-tertiary-shade) !important;

				.searchbar-input {
					border-radius: $jw-border-radius !important;
				}
			}
		}

		input::-webkit-search-cancel-button {
			-webkit-appearance: none;
		}

		.searchbar-clear-icon {
			color: var(--jw-color-secondary) !important;
		}
	}
}

.searchbar-container {
	position: relative;
	z-index: 10;
	padding-inline: 8px;

	.search-opener {
		width: 40px;
		height: 40px;
		display: flex;
		justify-content: center;
		align-items: center;
		padding: var(--space-x3, 12px) var(--space-x4, 16px);
		gap: var(--space-x2, 8px);
		border-radius: 60px;
		background: var(--ghost-ghost-16, hsla(0, 0%, 100%, 0.16));
		color: #fff;
		margin-inline-start: auto;
	}
}

.search-overlay {
	position: fixed;
	display: block;
	width: 100vw;
	height: 100vh;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	cursor: pointer;
	z-index: -1;
}
</style>
